import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { GraduationCap, BookOpen, Award, Brain, MessageSquare, CheckCircle, Clock, CheckSquare, Star, Shield, Zap, Target, Users, ChevronRight } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import PricingCardsSection from "@/components/PricingCardsSection";
import DarkModeToggle from "@/components/DarkModeToggle";
import { useTheme } from "@/contexts/ThemeContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_11207ce1-f0ad-4af8-83b6-64fb331f35a6/artifacts/e6s5tkbt_OSSuccess6.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Secondary CTA Configuration - Easy to change
const SECONDARY_CTA_LABEL = "Browse CE Courses";
const SECONDARY_CTA_PATH = "/courses";

// Icon mapping
const ICON_MAP = {
  Brain: Brain,
  BookOpen: BookOpen,
  Award: Award,
  MessageSquare: MessageSquare,
  CheckCircle: CheckCircle,
  Clock: Clock,
  GraduationCap: GraduationCap,
  Star: Star,
  Shield: Shield,
  Zap: Zap,
  Target: Target,
  Users: Users,
};

function DynamicIcon({ iconName, className, style }) {
  const IconComponent = ICON_MAP[iconName] || Brain;
  return <IconComponent className={className} style={style} />;
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [content, setContent] = useState(null);
  const { isDarkMode } = useTheme();

  // Theme-aware colors
  const colors = {
    heading: isDarkMode ? '#ffffff' : '#092036',
    subtext: isDarkMode ? '#e2e8f0' : '#70767C',
    link: isDarkMode ? '#93c5fd' : '#102862',
    linkBorder: isDarkMode ? 'rgba(147, 197, 253, 0.3)' : 'rgba(16, 40, 98, 0.3)',
    linkHoverBg: isDarkMode ? 'rgba(147, 197, 253, 0.1)' : 'rgba(16, 40, 98, 0.05)',
    linkHoverBorder: isDarkMode ? 'rgba(147, 197, 253, 0.5)' : 'rgba(16, 40, 98, 0.5)',
    paceText: isDarkMode ? '#ffffff' : '#000000',
    cardBg: isDarkMode ? '#0f2744' : 'white',
    sectionBg: isDarkMode ? '#092036' : '#f5f5f5',
    mainBg: isDarkMode ? '#092036' : '#ffffff',
    headerBg: isDarkMode ? '#092036' : 'rgba(255, 255, 255, 0.95)',
    headerBorder: isDarkMode ? 'transparent' : '#e5e7eb',
  };

  useEffect(() => {
    // Fetch featured courses (no auth required)
    fetch(`${API}/featured-courses`)
      .then(res => res.json())
      .then(data => setFeaturedCourses(data))
      .catch(err => console.error('Error fetching featured courses:', err));
    
    // Fetch landing page content
    fetch(`${API}/landing-page`)
      .then(res => res.json())
      .then(data => setContent(data))
      .catch(err => console.error('Error fetching landing page content:', err));
  }, []);

  // Default content while loading
  const heroTitle = content?.hero_title || "Oral Surgery Continuing Education for Dentists";
  const heroSubtitle = content?.hero_subtitle || "Practical, office-based Continuing Education (CE) designed for the cases you actually see.";
  const heroButtonText = content?.hero_button_text || "Create an Account and Start CE";
  const heroButtonSubtext = content?.hero_button_subtext || "Takes less than 30 seconds to begin.";
  const featuresTitle = content?.features_title || "Why Choose Oral Surgery Success?";
  const features = content?.features || [
    { icon: "Brain", title: "AI-Powered Learning", description: "Get instant, accurate answers to your questions using advanced RAG technology trained on oral surgery course materials.", color: "#102862" },
    { icon: "BookOpen", title: "Personalized Assessments", description: "AI generates assessment questions based on what you've been studying, ensuring relevant evaluation of your learning.", color: "#9E3639" },
    { icon: "Award", title: "AGD PACE Provider", description: "Earn AGD PACE approved continuing education online. At your own pace and at a time you choose.", color: "#102862" }
  ];
  const howItWorksTitle = content?.how_it_works_title || "How Oral Surgery Success Works";
  const howItWorksSubtitle = content?.how_it_works_subtitle || "Learn, earn CE credit, and get direct surgeon feedback with Curbside Consult.";
  const steps = content?.steps || [
    { step_number: 1, icon: "BookOpen", title: "Grab your all access pass and select a course", description: "Choose a topic like coronectomy, sedation, or complications. Start learning at your own pace and see how the platform works.", color: "#102862" },
    { step_number: 2, icon: "MessageSquare", title: "Study and get expert feedback", description: "Read articles or watch lessons while asking questions. With Curbside Consult, get direct feedback from a board-certified oral surgeon on your real cases.", color: "#9E3639" },
    { step_number: 3, icon: "CheckCircle", title: "Take a personalized assessment and earn CE", description: "Once you've met the requirements, take a tailored assessment. Pass, complete the short survey, and instantly download your AGD-approved completion letter.", color: "#102862" }
  ];
  const ctaTitle = content?.cta_title || "Ready to Advance Your Surgical Skills?";
  const ctaSubtitle = content?.cta_subtitle || "Join your dental colleagues advancing their skills through continuing education.";
  const ctaButtonText = content?.cta_button_text || "Start Learning Today";
  const footerText = content?.footer_text || "© 2025 Oral Surgery Success. All rights reserved.";
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: isDarkMode ? '#092036' : '#ffffff' }}>
      {/* Header */}
      <header 
        className={`fixed top-0 w-full z-50 ${isDarkMode ? '' : 'backdrop-blur-md border-b'}`} 
        style={{ 
          backgroundColor: isDarkMode ? '#092036' : 'rgba(255, 255, 255, 0.95)', 
          borderColor: colors.headerBorder 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 cursor-pointer" onClick={() => navigate("/")}>
            <img src={LOGO_URL} alt="Oral Surgery Success" className="h-10 w-10 sm:h-12 sm:w-12 object-contain flex-shrink-0" style={isDarkMode ? { filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.8))' } : {}} />
            <span className="text-base sm:text-2xl font-bold truncate" style={{ color: colors.heading }}>Oral Surgery Success</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <DarkModeToggle />
            <Button 
              data-testid="header-login-btn"
              onClick={() => navigate("/auth")}
              className="px-3 sm:px-6 py-2 rounded-full transition-all duration-300 hover:scale-105 shadow-lg text-white border-0 text-sm sm:text-base whitespace-nowrap"
              style={{ background: '#102862' }}
            >
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex justify-center mb-8">
            <img src={LOGO_URL} alt="Oral Surgery Success" className="h-32 w-32 object-contain" style={isDarkMode ? { filter: 'drop-shadow(0 0 3px rgba(255,255,255,0.8))' } : {}} />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" style={{ color: colors.heading }}>
            {heroTitle}
          </h1>
          <p className="text-lg sm:text-xl mb-10 max-w-4xl mx-auto leading-relaxed" style={{ color: colors.subtext }}>
            {heroSubtitle}
          </p>
          <div className="flex flex-col items-center gap-2 mb-16">
            <Button 
              data-testid="hero-get-started-btn"
              onClick={() => navigate("/auth?action=start")}
              className="px-10 py-6 text-lg rounded-full transition-all duration-300 hover:scale-105 shadow-2xl text-white border-0"
              style={{ background: '#FF6B35' }}
            >
              {heroButtonText}
            </Button>
            <p className="text-sm" style={{ color: colors.subtext }}>{heroButtonSubtext}</p>
            <Link 
              to={SECONDARY_CTA_PATH}
              className="group inline-flex items-center gap-2 mt-3 px-6 py-3 text-base font-medium rounded-full border transition-all duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{ 
                color: colors.link, 
                borderColor: colors.linkBorder,
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.linkHoverBg;
                e.currentTarget.style.borderColor = colors.linkHoverBorder;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = colors.linkBorder;
              }}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.linkBorder}`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {SECONDARY_CTA_LABEL}
              <ChevronRight 
                className="w-4 h-4 transition-transform duration-200 ease-out group-hover:translate-x-1" 
                style={{ color: colors.link }}
              />
            </Link>
          </div>
          
          {/* AGD PACE Badge in Hero */}
          <div className="flex justify-center mt-4">
            <div className="flex items-center gap-2 pace-section">
              <img 
                src="https://customer-assets.emergentagent.com/job_dentalce-hub/artifacts/20ojtj4k_agd-pace-logo-bw.png" 
                alt="AGD PACE Approved Provider" 
                className="h-24 object-contain flex-shrink-0 pace-logo"
                style={isDarkMode ? { filter: 'invert(1) contrast(1.1)' } : {}}
              />
              <div className="text-sm text-left pace-text" style={{ color: colors.paceText, fontFamily: 'Arial, Helvetica, sans-serif', lineHeight: '1.2' }}>
                <p className="font-bold">Oral Surgery Success</p>
                <p>Nationally Approved PACE Program Provider for FAGD/MAGD credit.</p>
                <p>Approval does not imply acceptance by any regulatory authority or AGD endorsement.</p>
                <p className="font-semibold">10/01/2025 to 09/30/2027</p>
                <p className="font-semibold">Provider ID #421884</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Courses Section */}
      {featuredCourses.length > 0 && (
        <section className="py-20 px-6" style={{ backgroundColor: colors.sectionBg }}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: colors.heading }}>
                Featured CE Courses
              </h2>
              <p className="text-lg" style={{ color: colors.subtext }}>
                Start your continuing education journey with our most popular courses
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" style={{ alignItems: 'stretch' }}>
              {featuredCourses.map((course) => (
                <Card 
                  key={course.id} 
                  className="overflow-hidden hover:shadow-2xl transition-all duration-300"
                  style={{ 
                    borderTop: '4px solid #102862', 
                    backgroundColor: colors.cardBg,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }}
                >
                  {/* Course Image/Icon */}
                  <div className="h-48 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #102862 0%, #1e3a8a 100%)', flexShrink: 0 }}>
                    {course.icon_url ? (
                      <img 
                        src={course.icon_url.startsWith('http') ? course.icon_url : `${API}${course.icon_url}`} 
                        alt={course.title} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <GraduationCap className="w-20 h-20 text-white opacity-80" />
                    )}
                  </div>

                  {/* Course Content */}
                  <div className="p-6" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="w-4 h-4" style={{ color: '#FF6B35' }} />
                      <span className="text-sm font-semibold" style={{ color: '#FF6B35' }}>
                        {course.ce_hours} CE {course.ce_hours === 1 ? 'Hour' : 'Hours'}
                      </span>
                      {course.is_paid ? (
                        <span className="ml-auto text-xs font-semibold px-2 py-1 rounded" style={{ background: isDarkMode ? '#1e3a8a' : '#dbeafe', color: isDarkMode ? '#93c5fd' : '#1e40af' }}>
                          MEMBERS ONLY
                        </span>
                      ) : (
                        <span className="ml-auto text-xs font-semibold px-2 py-1 rounded" style={{ background: isDarkMode ? '#065f46' : '#dcfce7', color: isDarkMode ? '#a7f3d0' : '#166534' }}>
                          FREE
                        </span>
                      )}
                    </div>

                    <h3 className="text-2xl font-bold mb-3" style={{ color: colors.heading }}>
                      {course.title}
                    </h3>

                    <p className="text-sm mb-4 line-clamp-2" style={{ color: colors.subtext }}>
                      {course.description}
                    </p>

                    {/* Learning Outcomes */}
                    {course.learning_outcomes && course.learning_outcomes.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold mb-2" style={{ color: colors.heading }}>
                          What You&apos;ll Learn:
                        </h4>
                        <ul className="space-y-1">
                          {course.learning_outcomes.slice(0, 5).map((outcome, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: colors.subtext }}>
                              <CheckSquare className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#16A34A' }} />
                              <span>{outcome}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* CTA Button - Pushed to bottom with marginTop auto */}
                    <div style={{ marginTop: 'auto' }}>
                      <Button
                        onClick={() => navigate(`/course-preview/${course.id}`)}
                        className="w-full py-6 text-base rounded-lg transition-all duration-300 hover:scale-105 shadow-lg text-white border-0"
                        style={{ background: !course.is_paid ? '#16a34a' : '#102862' }}
                      >
                        {!course.is_paid ? 'Start Free Course' : 'Preview Course'}
                      </Button>
                      {/* Fixed height container for microcopy to keep buttons aligned */}
                      <div style={{ height: '24px', marginTop: '8px' }}>
                        {course.is_paid && (
                          <p className="text-center text-xs" style={{ color: colors.subtext }}>
                            Included with All Access Pass
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Browse All Courses Link */}
            <div className="text-center mt-12">
              <Link
                to="/courses"
                className="text-lg font-medium hover:underline transition-all inline-flex items-center gap-1"
                style={{ color: colors.link }}
              >
                Browse CE Courses
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="py-20 px-6" style={{ backgroundColor: colors.mainBg }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16" style={{ color: colors.heading }}>
            {featuresTitle}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 flex flex-col" 
                style={{ borderLeft: `4px solid ${feature.color}`, backgroundColor: colors.cardBg }}
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-6" 
                  style={{ background: feature.color }}
                >
                  <DynamicIcon iconName={feature.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 min-h-[72px] md:min-h-[80px]" style={{ color: colors.heading }}>{feature.title}</h3>
                <p className="leading-relaxed flex-grow" style={{ color: colors.subtext }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingCardsSection />

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-6" style={{ backgroundColor: colors.sectionBg }}>
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: colors.heading }}>
              {howItWorksTitle}
            </h2>
            <p className="text-lg" style={{ color: colors.subtext }}>
              {howItWorksSubtitle}
            </p>
          </div>

          {/* Steps */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {steps.map((step, index) => (
              <div key={index} className="p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col" style={{ backgroundColor: colors.cardBg }}>
                <div className="flex items-center gap-2 mb-4">
                  <span 
                    className="text-sm font-bold px-3 py-1 rounded-full text-white" 
                    style={{ background: step.color }}
                  >
                    Step {step.step_number}
                  </span>
                </div>
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-6" 
                  style={{ background: isDarkMode ? '#1e3a5f' : '#F4F4F4' }}
                >
                  <DynamicIcon iconName={step.icon} className="w-8 h-8" style={{ color: step.color }} />
                </div>
                <h3 className="text-xl font-bold mb-3 min-h-[72px] md:min-h-[84px]" style={{ color: colors.heading }}>
                  {step.title}
                </h3>
                <p className="leading-relaxed flex-grow" style={{ color: colors.subtext }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>

          {/* Small CTA */}
          <div className="text-center">
            <p className="text-lg mb-4" style={{ color: colors.heading }}>
              Ready to see it in action?
            </p>
            <Link 
              to="/courses"
              className="inline-flex items-center gap-2 px-8 py-5 text-base font-medium rounded-full transition-all duration-300 hover:scale-105 shadow-lg border-0 text-white"
              style={{ background: '#FF6B35' }}
            >
              Browse CE Courses
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto rounded-3xl p-12 text-center shadow-2xl" style={{ background: 'linear-gradient(135deg, #102862 0%, #092036 100%)' }}>
          <h2 className="text-4xl font-bold text-white mb-6">
            {ctaTitle}
          </h2>
          <p className="text-xl mb-4 text-white">
            {ctaSubtitle}
          </p>
          <p className="text-sm mb-8 text-white/70">
            AGD PACE approved provider. Board-certified oral surgeon. 23+ years in private practice.
          </p>
          <Button 
            data-testid="cta-start-learning-btn"
            onClick={() => navigate("/auth?action=start")}
            className="px-10 py-6 text-lg rounded-full transition-all duration-300 hover:scale-105 shadow-xl font-semibold border-0"
            style={{ backgroundColor: 'white', color: '#092036' }}
          >
            {ctaButtonText}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 text-center" style={{ background: isDarkMode ? '#051020' : '#092036' }}>
        <p className="text-white">{footerText}</p>
      </footer>
    </div>
  );
}
